import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import {
  selectToggleToFavoriteLoading,
  setSelectedCollection,
} from "../../store/slices/model/slice";
import {
  ModalContainer,
  Button,
  Buttons,
  CloseButton,
  VideoFooter,
  ModelName,
  VideoElement,
  StyledButton2,
  ThumbnailVideo,
  VideoContainer,
  FavoriteButton,
  MessageButton,
  ModelSeed,
  FolderButton,
  SettingsButton,
} from "./CollectionModal.styles";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as DownloadSvg } from "../../assets/icons/download.svg";
import { ReactComponent as DeleteSvg } from "../../assets/icons/delete.svg";
import { ReactComponent as WhiteStarSvg } from "../../assets/icons/whiteStar.svg";
import { ReactComponent as WhiteFavoriteIcon } from "../../assets/icons/whiteFavorites.svg";
import { ReactComponent as FilledFavoriteIcon } from "../../assets/icons/filledFavorites.svg";
import { ReactComponent as MessageIcon } from "../../assets/icons/message.svg";
import { ReactComponent as FolderIcon } from "../../assets/icons/folder.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/settings.svg";
import { ReactComponent as LeafIcon } from "../../assets/icons/leaf.svg";

import { formatModelName } from "../../utils/utils";
import { selectUser } from "../../store/slices/user/slice";
import {
  MEDIA_TYPE,
  NUDIFY_MODEL_NAME,
  NUDIFY_PRICE,
  REGENERATE_DISCOUNT_PERCENTAGE,
  VIDEO_DURATION_PRICE,
  VIDEO_FPS_PRICE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import { ImageElement } from "../utils/CollectionItem/CollectionItem.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { toggleToFavorite } from "../../store/slices/model/asyncThunks";
import { toast } from "sonner";

const CollectionModal = ({
  isOpen,
  onClose,
  video,
  onOpenVideoModal,
  onDeleteClick,
  onRateClick,
  onFolderClick,
  onSettingsClick,
  setCustomPrompt,
}) => {
  useLockBodyScroll(isOpen);
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const loading = useSelector(selectToggleToFavoriteLoading);

  const user = useSelector(selectUser);

  const { ref: videoContainerRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: "100px",
    triggerOnce: true,
  });

  const handleFavoriteToggle = () => {
    if (!loading && video._id) {
      dispatch(toggleToFavorite({ collectionId: video._id }));
    }
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleGenerateAgain = () => {
    if (!video) return;

    const modelObject = {
      ...video,
      regenerate: true,
      collectionId: video._id,
    };

    dispatch(setSelectedCollection(modelObject));
    onClose();
    if (onOpenVideoModal) {
      onOpenVideoModal();
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteClick) {
      onDeleteClick();
    }
  };

  const handleRateClick = () => {
    if (onRateClick) {
      onRateClick();
    }
  };

  const handleFolderClick = () => {
    if (onFolderClick) {
      onFolderClick();
    }
  };

  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
      setCustomPrompt(video?.customPrompt);
    }
  };

  const handleDownload = async () => {
    let mediaUrl = null;
    if (video.output.type === MEDIA_TYPE.IMAGE) {
      mediaUrl = video.output.image.url;
    } else {
      mediaUrl = video.output.video.url;
    }

    if (!mediaUrl) return;

    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);

    if (isMobile) {
      //   window.open(mediaUrl, "_blank");
      window.location.href = mediaUrl;
      return;
    }

    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const extension = new URL(mediaUrl).pathname
        .split(".")
        .pop()
        .toLowerCase();

      const modelName = video.name;
      const safeName = modelName.replace(/[<>:"/\\|?*]+/g, "_");

      link.download = `${user.username}_${safeName}.${extension}`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download video:", err);
    }
  };

  const copyToClipBoard = async () => {
    if (video.seed) {
      try {
        await navigator.clipboard.writeText(video.seed);
        console.log("Copied to clipboard:", video.seed);
        toast.success("Copied to clipboard");
      } catch (err) {
        console.error("Failed to copy link:", err);
      }
    }
  };

  if (!video) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3000 }}
    >
      <ModalContainer ref={videoContainerRef}>
        <ModelName>{formatModelName(video?.name)}</ModelName>
        {!!video.seed && (
          <ModelSeed onClick={copyToClipBoard}>
            <LeafIcon />
            {video.seed}
          </ModelSeed>
        )}

        <CloseButton onClick={onClose}>
          <CloseSvg />
        </CloseButton>

        <VideoContainer>
          {video?.output?.type === MEDIA_TYPE.IMAGE ? (
            <ImageElement src={video.output.image.url} />
          ) : (
            <>
              <ThumbnailVideo
                src={
                  video?.input?.image?.resizedImage?.url ||
                  video?.input?.image?.url
                }
                alt={video?.modelName || "Video thumbnail"}
                $loaded={videoLoaded}
                $blur={!videoLoaded}
              />
              {inView && (
                <VideoElement
                  ref={videoRef}
                  controls={false}
                  onLoadedData={handleVideoLoad}
                  muted
                  loop
                  playsInline
                  poster={
                    video?.input?.image?.resizedImage?.url ||
                    video?.input?.image?.url
                  }
                  autoPlay
                  preload="auto"
                  $loaded={videoLoaded}
                >
                  <source
                    src={
                      video?.output?.video?.compressedUrl ||
                      video?.output?.video?.url
                    }
                    type="video/mp4"
                  />
                </VideoElement>
              )}
            </>
          )}
        </VideoContainer>

        {!!video?.customPrompt && (
          <SettingsButton onClick={handleSettingsClick}>
            <SettingsIcon />
          </SettingsButton>
        )}
        <FolderButton onClick={handleFolderClick}>
          <FolderIcon />
        </FolderButton>

        <MessageButton onClick={handleRateClick}>
          <MessageIcon />
        </MessageButton>

        <FavoriteButton onClick={handleFavoriteToggle}>
          {user.favoriteCollections.includes(video._id) ? (
            <FilledFavoriteIcon />
          ) : (
            <WhiteFavoriteIcon />
          )}
        </FavoriteButton>

        <VideoFooter>
          {/* <Date>{format(video.createdAt, "MM/dd/yyyy, h:mm a")}</Date> */}

          <Buttons>
            <StyledButton2
              style={{ whiteSpace: "nowrap" }}
              onClick={handleDownload}
            >
              <DownloadSvg width={20} height={20} />
            </StyledButton2>
            <Button style={{ flex: 1 }} onClick={handleGenerateAgain}>
              {/* <RefreshSvg /> */}
              Generate Again
              <WhiteStarSvg style={{ marginRight: "-6px" }} />
              {video.modelName === NUDIFY_MODEL_NAME
                ? Math.ceil(video.price * (1 - REGENERATE_DISCOUNT_PERCENTAGE))
                : Math.ceil(
                    (video.price +
                      (video.nudify ? NUDIFY_PRICE : 0) +
                      VIDEO_RESOLUTION_PRICE[video.output.resolution] +
                      (VIDEO_DURATION_PRICE[video.output.videoDuration] ?? 0) +
                      (VIDEO_FPS_PRICE[video.output.fps] ?? 0)) *
                      (1 - REGENERATE_DISCOUNT_PERCENTAGE)
                  )}
            </Button>

            {/* <TooltipWrapper
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <StyledButton2
                style={{ whiteSpace: "nowrap" }}
                onClick={handleGenerateAgain}
              >
                <OpenSvg width={24} height={24} />
              </StyledButton2>
              {showTooltip && <Tooltip>Generate</Tooltip>}
            </TooltipWrapper> */}
            <Button style={{ borderRadius: "50%" }} onClick={handleDeleteClick}>
              <DeleteSvg />
            </Button>
          </Buttons>
        </VideoFooter>
      </ModalContainer>
    </Modal>
  );
};

export default CollectionModal;
